import { all, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import CoachingFile from "@app/scene/dashboard/types/CoachingFile";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";

import {
  deleteCoachingFile,
  fetchCoachingFile,
  fetchCoachingFiles,
  updateCoachingFileTags,
  uploadCoachingFiles,
} from "./actions";

const COACHING_FILES_PAGE_SIZE = 1000;

function* onFetchCoachingFiles({ payload }: ReturnType<typeof fetchCoachingFiles.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const $filters: Record<string, unknown> = {
      subjectUserId: { $eq: payload.subjectUserId },
    };
    if (payload.tagIds && payload.tagIds.length > 0) {
      $filters.tagIds = { $in: payload.tagIds };
    }
    const requestPayload = {
      $filters,
      $limit: COACHING_FILES_PAGE_SIZE,
      $order: "-createdAt",
    };
    const firstPage: ListResponse<CoachingFile> = yield callApi(
      "listCoachingFiles",
      orgId,
      requestPayload
    );
    const data: CoachingFile[] = [...firstPage.data];
    const apiCalls = [];
    if (firstPage.total > COACHING_FILES_PAGE_SIZE) {
      for (let i = 1; i < Math.ceil(firstPage.total / COACHING_FILES_PAGE_SIZE); i += 1) {
        apiCalls.push(
          callApi("listCoachingFiles", orgId, {
            ...requestPayload,
            $offset: COACHING_FILES_PAGE_SIZE * i,
          })
        );
      }
    }
    const remainingPages: ListResponse<CoachingFile>[] = yield all(apiCalls);
    remainingPages.forEach((page) => {
      data.push(...page.data);
    });
    yield put(fetchCoachingFiles.success(data));
  } catch (error) {
    yield put(fetchCoachingFiles.failure());
    yield put(handleError({ error }));
  }
}

function* onUploadCoachingFiles({ payload }: ReturnType<typeof uploadCoachingFiles.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const uploaded: CoachingFile[] = yield all(
      payload.files.map((file) => callApi("createCoachingFile", orgId, file, payload.subjectUserId))
    );
    yield put(uploadCoachingFiles.success(uploaded));
  } catch (error) {
    yield put(uploadCoachingFiles.failure());
    yield put(handleError({ error }));
  }
}

function* onUpdateCoachingFileTags({ payload }: ReturnType<typeof updateCoachingFileTags.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updated: CoachingFile = yield callApi("updateCoachingFile", orgId, payload.id, {
      tagIds: payload.tagIds,
    });
    yield put(updateCoachingFileTags.success(updated));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(updateCoachingFileTags.failure(payload.id));
  }
}

function* onFetchCoachingFile({ payload }: ReturnType<typeof fetchCoachingFile>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const file: CoachingFile = yield callApi("fetchCoachingFile", orgId, payload.coachingFileId);
    payload.onSuccess(file);
  } catch (error) {
    payload.onFailure?.();
    yield put(handleError({ error }));
  }
}

function* onDeleteCoachingFile({ payload }: ReturnType<typeof deleteCoachingFile.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteCoachingFile", orgId, payload.id);
    yield put(deleteCoachingFile.success({ id: payload.id }));
  } catch (error) {
    yield put(deleteCoachingFile.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* coachingFilesSagas() {
  yield takeLatest(fetchCoachingFiles.request, onFetchCoachingFiles);
  yield takeEvery(fetchCoachingFile, onFetchCoachingFile);
  yield takeEvery(uploadCoachingFiles.request, onUploadCoachingFiles);
  yield takeEvery(updateCoachingFileTags.request, onUpdateCoachingFileTags);
  yield takeEvery(deleteCoachingFile.request, onDeleteCoachingFile);
}
